import React, { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import { ExpandHeader, ToolCallWrapper } from '../shared/ToolCallComponents';
import ToolCall from '../ToolCall';
import { CollapsibleSection } from './shared/CollapsibleSection';
import Text from '../../Text/Text';

interface Message {
    type: 'text' | 'toolCall';
    text?: string;
    name?: string;
    args?: any;
    status?: 'init' | 'finish';
    id?: string;
}

interface GatherContextResults {
    searchResults?: Record<string, { name: string; url: string; text: string; thumbnailUrl?: string }>;
    fileResults?: Record<string, string>;
    seenFilePathsArr?: string[];
    seenSearchInternetResultsArr?: string[];
    reflection?: string;
}

interface GatherContextProps {
    item: {
        messages?: Message[];
        id?: string;
        name?: string;
        args?: any;
        gatherContextResults?: GatherContextResults;
        chronologicalEvents?: Array<{
            type: 'chunk' | 'toolCall';
            content?: string;
            message?: Message;
        }>;
    };
    isInProgress: boolean;
}

export const GatherContext: React.FC<GatherContextProps> = ({ item, isInProgress }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    useEffect(() => {
        if (isInProgress) {
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
    }, [isInProgress]);

    const getToolCallItem = (message: Message, isParentInProgress: boolean) => ({
        id: message.id,
        name: message.name,
        args: message.args,
        status: message.status,
        isInProgress: isParentInProgress && message.status === 'init'
    });

    const renderSearchResults = (results: Record<string, { name: string; url: string; text: string; thumbnailUrl?: string }>, title: string) => {
        const entries = Object.entries(results);
        if (entries.length === 0) return null;

        return (
            <CollapsibleSection
                title={`${title} (${entries.length})`}
                collapseOnContent={false}
                initialExpanded={false}
            >
                <div className="space-y-2">
                    {entries.map(([url, result], index) => (
                        <div
                            key={index}
                            className="flex items-center py-2 px-3 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150 border border-neutral-200 dark:border-neutral-800"
                        >
                            <img
                                src={`https://icons.duckduckgo.com/ip3/${new URL(url).hostname}.ico`}
                                alt="Site icon"
                                className="w-4 h-4 mr-3 flex-shrink-0"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150 truncate">
                                    <Text item={{ text: `[${result.name}](${url})` }} index={0} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </CollapsibleSection>
        );
    };

    const renderFiles = (fileResults: Record<string, string>) => {
        const entries = Object.entries(fileResults);
        if (entries.length === 0) return null;

        return (
            <CollapsibleSection
                title={`Files (${entries.length})`}
                collapseOnContent={false}
                initialExpanded={false}
            >
                <div className="space-y-2">
                    {entries.map(([filePath], index) => (
                        <div
                            key={index}
                            className="flex items-center py-1 px-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150"
                        >
                            <span className="text-neutral-400 dark:text-neutral-500 mr-2">•</span>
                            <div className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150">
                                <Text item={{ text: `[${filePath}](${filePath})` }} index={0} />
                            </div>
                        </div>
                    ))}
                </div>
            </CollapsibleSection>
        );
    };

    const renderSourcesArray = (sources: string[], title: string) => {
        if (!sources || sources.length === 0) return null;

        return (
            <CollapsibleSection
                title={`${title} (${sources.length})`}
                collapseOnContent={false}
                initialExpanded={false}
            >
                <div className="space-y-2">
                    {sources.map((url, index) => {
                        // Try to get the name from searchResults if available
                        const searchResult = item.gatherContextResults?.searchResults?.[url];
                        const displayName = searchResult?.name || 'source';

                        return (
                            <div
                                key={index}
                                className="flex items-center py-1 px-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150"
                            >
                                <span className="text-neutral-400 dark:text-neutral-500 mr-2">•</span>
                                <div
                                    className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150">
                                    <Text item={{text: `[${displayName}](${url})`}} index={0}/>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CollapsibleSection>
        );
    };

    const renderFilesArray = (files: string[]) => {
        if (!files || files.length === 0) return null;

        return (
            <CollapsibleSection
                title={`Files Encountered (${files.length})`}
                collapseOnContent={false}
                initialExpanded={false}
            >
                <div className="space-y-2">
                    {files.map((filePath, index) => (
                        <div
                            key={index}
                            className="flex items-center py-1 px-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150"
                        >
                            <span className="text-neutral-400 dark:text-neutral-500 mr-2">•</span>
                            <div
                                className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150">
                                <Text item={{text: `[${filePath}](${filePath})`}} index={0}/>
                            </div>
                        </div>
                    ))}
                </div>
            </CollapsibleSection>
        );
    };


    const ContextSection: React.FC<{
        title: string;
        fileResults?: Record<string, string>;
        searchResults?: Record<string, { name: string; url: string; text: string; thumbnailUrl?: string }>;
        seenFilePathsArr?: string[];
        seenSearchInternetResultsArr?: string[];
    }> = ({ title, fileResults, searchResults, seenFilePathsArr, seenSearchInternetResultsArr }) => {
        const hasContent = (fileResults || searchResults || seenFilePathsArr || seenSearchInternetResultsArr) && !isInProgress;

        if (!hasContent) return null;

        return (
            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
                <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                    {title}:
                </div>
                <div className="space-y-3">
                    {seenFilePathsArr && renderFilesArray(seenFilePathsArr)}
                    {seenSearchInternetResultsArr && renderSourcesArray(seenSearchInternetResultsArr, "Internet Sources Encountered")}
                    {fileResults && renderFiles(fileResults)}
                    {searchResults && renderSearchResults(searchResults, "Internet Sources")}
                </div>
            </div>
        );
    };

    const getTitle = () => {
        if (item.name === 'deepResearch') {
            return 'Deep Researching';
        }
        return 'Gathering Context';
    };

    return (
        <ToolCallWrapper>
            <ExpandHeader
                icon={<FaSearch className="mr-2 text-neutral-500 dark:text-neutral-400" size={14} />}
                title={getTitle()}
                isExpanded={isExpanded}
                isInProgress={isInProgress}
                onClick={() => setIsExpanded(prev => !prev)}
            />
            {isExpanded && (
                <div className="mt-2 px-2 space-y-4">
                    {/* Task display for deepResearch */}
                    {item.name === 'deepResearch' && item.args?.task && (
                        <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
                            <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                                Research Task:
                            </div>
                            <div className="py-2 px-3 bg-green-50 dark:bg-green-900/20 rounded-md border-l-4 border-green-300 dark:border-green-600">
                                <div className="text-sm text-neutral-700 dark:text-neutral-300">
                                    {item.args.task}
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Chronological display of chunks and tool calls */}
                    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
                        {item.chronologicalEvents && item.chronologicalEvents.length > 0 ? (
                            <div className="space-y-3">
                                {item.chronologicalEvents.map((event, index) => {
                                    const isLastEvent = index === item.chronologicalEvents!.length - 1;
                                    const shouldDefaultOpen = isLastEvent && isInProgress;

                                    return (
                                        <div key={index}>
                                            {event.type === 'chunk' ? (
                                                <CollapsibleSection
                                                    title="Thoughts"
                                                    collapseOnContent={false}
                                                    initialExpanded={true}
                                                    isLoading={shouldDefaultOpen}
                                                >
                                                    <div className="text-neutral-600 dark:text-neutral-400 italic">
                                                        {event.content}
                                                    </div>
                                                </CollapsibleSection>
                                            ) : event.type === 'toolCall' && event.message?.name ? (
                                                <ToolCall
                                                    item={getToolCallItem(event.message, isInProgress)}
                                                    isInProgress={isInProgress && event.message.status === 'init'}
                                                />
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : !isInProgress ? (
                            <div className="text-neutral-600 dark:text-neutral-400 italic">
                                No additional context needed
                            </div>
                        ) : null}
                    </div>

                    {/* Context Evaluated - sources encountered during search */}
                    <ContextSection
                        title="Context Evaluated"
                        seenFilePathsArr={item.gatherContextResults?.seenFilePathsArr}
                        seenSearchInternetResultsArr={item.gatherContextResults?.seenSearchInternetResultsArr}
                    />

                    {/* Context Kept - actual results */}
                    <ContextSection
                        title="Context Kept"
                        fileResults={item.gatherContextResults?.fileResults}
                        searchResults={item.gatherContextResults?.searchResults}
                    />

                    {/* Reflection - insights from the context gathering process */}
                    {item.gatherContextResults?.reflection && !isInProgress && (
                        <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
                            <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                                Reflection:
                            </div>
                            <div className="py-2 px-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border-l-4 border-blue-300 dark:border-blue-600">
                                <div className="text-sm text-neutral-700 dark:text-neutral-300">
                                    {item.gatherContextResults.reflection}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </ToolCallWrapper>
    );
};
